import type { BasePagingResponse, BaseResponse } from "../../../../lib/api-contract";
import type { DataTableDateRangeValue, DataTableNumberRangeValue } from "../../../../components/table";
import type { SurveyQuestion } from "./surveyQuestion.types";

export type {
  BaseQuestionData,
  BaseSurveyQuestion,
  CheckboxGridSurveyQuestion,
  ChoiceQuestionData,
  ContactQuestionData,
  DateQuestionData,
  DateSurveyQuestion,
  DateTimeRangeQuestionData,
  DateTimeQuestionData,
  DateTimeSurveyQuestion,
  DropdownQuestionData,
  DropdownSurveyQuestion,
  EmailQuestionData,
  EmailSurveyQuestion,
  FileUploadQuestionData,
  FileUploadSurveyQuestion,
  GridColumn,
  GridQuestionData,
  GridRow,
  LongAnswerSurveyQuestion,
  MultipleAnswerQuestionData,
  MultipleAnswerSurveyQuestion,
  MultipleChoiceGridSurveyQuestion,
  MultipleChoiceSurveyQuestion,
  NumericQuestionData,
  NumberQuestionData,
  NumberSurveyQuestion,
  PhoneQuestionData,
  PhoneSurveyQuestion,
  QuestionData,
  QuestionOption,
  RatingQuestionData,
  RatingSurveyQuestion,
  ScaleQuestionData,
  ScaleSurveyQuestion,
  ShortAnswerSurveyQuestion,
  SurveyQuestion,
  SurveyQuestionForType,
  SurveyQuestionTypeMap,
  TextQuestionData,
  TimeQuestionData,
  TimeSurveyQuestion,
  TypedSurveyQuestion,
  YesNoQuestionData,
  YesNoSurveyQuestion,
} from "./surveyQuestion.types";

export interface SurveyDateFilter {
  relative: "today" | "this_week" | "this_month" | "this_year" | "weekend" | "upcoming" | "past";
  timezone_offset: number;
}

export interface SurveyNumberRangeFilter {
  min: number | null;
  max: number | null;
}

export interface SurveyDateRangeFilter {
  from: string;
  to: string;
}

export interface SurveyStatisticsResponse {
  total_surveys: number;
  open_surveys: number;
  closed_surveys: number;
  posted_this_week: number;
}

export interface SurveyFilter {
  status?: "open" | "closed" | "unpublished";
  keyword?: string;
  title?: string;
  description?: string;
  points?: SurveyNumberRangeFilter;
  end_time?: SurveyDateRangeFilter;
  date_filter?: SurveyDateFilter;
}

export type SurveySortKey =
  | "newest_first"
  | "oldest_first"
  | "least_points"
  | "most_points"
  | "most_responses"
  | "created_at"
  | "points"
  | "responses_count"
  | "question_count";

/** Mirrors Backend's GetSurveyListForAdminRequest query contract. */
export interface GetSurveyListForAdminRequest {
  keyword?: string;
  title?: string;
  description?: string;
  point_min?: number;
  point_max?: number;
  end_time_from?: string;
  end_time_to?: string;
  status?: SurveyStatus;
  index: number;
  size: number;
  sort_key?: SurveySortKey;
  sort_direction?: "asc" | "desc";
}

export interface SurveyListFilters {
  status?: SurveyStatus;
  title?: string;
  description?: string;
  points?: DataTableNumberRangeValue;
  end_time?: DataTableDateRangeValue;
  date_filter?: {
    relative?: "today" | "this_week" | "this_month" | "this_year" | "weekend" | "upcoming" | "past";
    timezone_offset?: number;
    from?: string;
    to?: string;
  };
}

export interface SurveyListSorting {
  field: string;
  direction: "asc" | "desc";
  sortKey?: SurveySortKey;
}

export interface GetSurveyStatisticsRequest {
  from: string;
  to: string;
}

export interface SurveySorting {
  field: string;
  direction: "asc" | "desc";
}

export interface SurveyPagination {
  index: number;
  size: number;
}

export enum SurveyStatus {
  Open = "open",
  Closed = "closed",
  Unpublished = "unpublished",
}

/** Mirrors Backend's SurveyItemResponse list fields. */
export interface SurveyItemResponse {
  id: string;
  title: string;
  description: string | null;
  end_time: string;
  organization_id: string;
  points: number;
  status: SurveyStatus | null;
}

/** Mirrors Backend's SurveyListItemForAdminResponse. */
export interface SurveyListItemForAdminResponse extends SurveyItemResponse {
  question_count: number;
  responses_count: number;
}

/** @deprecated Legacy merchant list response; do not use for portal survey list. */
export interface SurveyPaging {
  count: number;
  index: number;
  size: number;
}

/** @deprecated Use SurveyAdminDetailResponse for portal survey detail data. */
export interface SurveyItem {
  category: string;
  status: SurveyStatus;
  questions: SurveyQuestion[];
  isPublished: boolean;
  publish_time: string | null;
  created_at: string;
  id: string;
  title: string;
  description: string;
  end_time: string;
  organization_id: string;
  points: number;
  total_responses?: number;
  deleteable?: boolean;
}

/** Mirrors Backend's AdminSurveyItemResponse. */
export interface SurveyAdminDetailResponse extends SurveyItemResponse {
  category: string;
  questions: SurveyQuestion[];
  isPublished: boolean;
  publish_time: string | null;
  created_at: string;
}

/** @deprecated Legacy merchant list response; do not use for portal survey list. */
export interface SurveyListResponse {
  paging: SurveyPaging;
  list: SurveyItem[] | null;
}

export interface SurveyListRequest {
  filter: SurveyFilter;
  pagination: SurveyPagination;
  sortKey?: SurveySortKey;
  sortOrder?: "asc" | "desc";
}

export interface SurveyBasicInfoFormValues {
  title: string;
  description: string;
  category: string;
  points: number;
  endTime: Date | undefined;
}

export interface SurveyFormValues extends SurveyBasicInfoFormValues {
  questions: SurveyQuestion[];
}

export type SurveyQuestionRequest = Omit<SurveyQuestion, "client_id">;

export interface SurveyRequestFields {
  title: string;
  description: string;
  category: string;
  points: number;
  end_time: string;
  questions: SurveyQuestionRequest[];
}

export interface CreateSurveyRequest extends SurveyRequestFields {}

export interface UpdateSurveyRequest extends Partial<SurveyRequestFields> {
  is_published?: boolean;
  publish_time?: string;
}

export type SurveyMutationResponseData = SurveyItemResponse;

export interface SurveyMutationResponse {
  success: boolean;
  message: string;
  user_message: string;
  data: SurveyMutationResponseData;
}

export interface SurveyPublishRequest {
  is_published: boolean;
}

export interface SurveyPublishResponse {
  success: boolean;
  message: string;
  user_message: string;
  data: SurveyMutationResponseData;
}
export interface SurveySummaryOption {
  option_id: string;
  label: string;
  count: number;
}

export interface SurveyTextStatistics {
  kind: "text";
  unique_count: number;
  answers: SurveySummaryOption[];
}

export interface SurveyChoiceStatistics {
  kind: "choice";
  options: SurveySummaryOption[];
}

export interface SurveyBooleanStatistics {
  kind: "boolean";
  yes_count: number;
  no_count: number;
  options: SurveySummaryOption[];
}

export interface SurveyNumericStatistics {
  kind: "numeric";
  average: number;
  min: number | null;
  max: number | null;
  distribution: SurveySummaryOption[];
  options: SurveySummaryOption[];
}

export interface SurveyTemporalStatistics {
  kind: "temporal";
  distribution: SurveySummaryOption[];
  options: SurveySummaryOption[];
}

export interface SurveyResponseCountStatistics {
  kind: "file" | "grid";
  response_count: number;
}

export type SurveyQuestionStatistics =
  | SurveyTextStatistics
  | SurveyChoiceStatistics
  | SurveyBooleanStatistics
  | SurveyNumericStatistics
  | SurveyTemporalStatistics
  | SurveyResponseCountStatistics;

export interface SurveySummaryQuestion {
  question_id: string;
  type: string;
  position: number;
  title: string;
  answered_count: number;
  unanswered_count?: number;
  options: SurveySummaryOption[];
  statistics?: SurveyQuestionStatistics;
  total_pdf?: number;
  total_image?: number;
  total_document?: number;
  total_other?: number;
}

export interface SurveySummaryData {
  title: string;
  survey_id: string;
  total_responses: number;
  questions: SurveySummaryQuestion[];
}

export interface SurveySummaryResponse {
  success: boolean;
  http_request_status: number;
  message: string;
  user_message: string;
  data: SurveySummaryData;
}

export interface SurveyRepository {
  getSurveyList(
    request: GetSurveyListForAdminRequest,
  ): Promise<BasePagingResponse<SurveyListItemForAdminResponse>>;
  deleteSurvey(id: string): Promise<BaseResponse<object>>;
  createSurvey(request: CreateSurveyRequest): Promise<SurveyMutationResponse>;
  updateSurvey(id: string, request: UpdateSurveyRequest): Promise<SurveyMutationResponse>;
  getSurveyDetail(id: string): Promise<SurveyAdminDetailResponse>;
  publishSurvey(id: string, request: SurveyPublishRequest): Promise<SurveyPublishResponse>;
  getSurveySummary(id: string): Promise<SurveySummaryResponse>;
  getSurveyStatistics(
    request: GetSurveyStatisticsRequest,
    signal?: AbortSignal,
  ): Promise<SurveyStatisticsResponse>;
}
